/*
 * HistoHead: the "Track Your Order" page (sections/hh-track-order.liquid).
 *
 * The page asks the relay through the store's app proxy (POST /apps/hh/track with the order number and
 * the checkout email or the delivery postcode) and draws the answer. The relay states facts; every
 * sentence a customer reads lives here, so the wording can change without touching the service.
 *
 * Opening the page as /pages/track-your-order#o=HH1290&k=32773 (the link in the shipping email) fills
 * the form in and asks straight away; the fragment never leaves the browser.
 */
(function () {
  'use strict';
  if (customElements.get('hh-track-order')) return;

  var STEPS = [
    { key: 'ordered', label: 'Ordered' },
    { key: 'production', label: 'In production' },
    { key: 'shipped', label: 'Shipped' },
    { key: 'in_transit', label: 'In transit' },
    { key: 'delivered', label: 'Delivered' }
  ];
  var REGION_NAMES = { US: 'the US', CA: 'Canada', GB: 'the United Kingdom', EU: 'Europe' };
  var DAY = 86400000;
  var locale = document.documentElement.lang || 'en';

  /* ---------- small helpers ---------- */

  function esc(value) {
    return String(value === null || value === undefined ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function parseDate(iso) {
    var date = iso ? new Date(iso) : null;
    return date && !isNaN(date.getTime()) ? date : null;
  }
  function fmtDate(iso) {
    var date = parseDate(iso);
    if (!date) return '';
    var options = { month: 'short', day: 'numeric' };
    if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
    return date.toLocaleDateString(locale, options);
  }
  function fmtDateTime(iso) {
    var date = parseDate(iso);
    if (!date) return '';
    var options = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
    return date.toLocaleString(locale, options);
  }
  function daysSince(iso) {
    var date = parseDate(iso);
    return date ? Math.max(0, Math.floor((Date.now() - date.getTime()) / DAY)) : null;
  }
  function plusDays(iso, days) {
    var date = parseDate(iso);
    return date ? new Date(date.getTime() + days * DAY).toISOString() : null;
  }
  function plural(n, word) {
    return n + ' ' + word + (n === 1 ? '' : 's');
  }
  function words(n) {
    return ['no', 'one', 'two', 'three', 'four', 'five', 'six'][n] || String(n);
  }
  function capital(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /* ---------- the figures ---------- */

  // median to 80th percentile as the range, 95th as "a few took up to"
  function rangeOf(figure) {
    var low = Math.round(figure.p50);
    var high = Math.round(figure.p80);
    return low === high ? 'about ' + plural(low, 'day') : low + ' to ' + high + ' days';
  }
  function upToOf(figure) {
    return Math.ceil(figure.p95);
  }
  function weeksOf(timing) {
    return Math.round((timing.windowDays || 56) / 7);
  }
  function regionName(facts) {
    if (REGION_NAMES[facts.order.region]) return REGION_NAMES[facts.order.region];
    var parts = (facts.order.town || '').split(',');
    return parts.length ? parts[parts.length - 1].trim() : 'your country';
  }
  // "DHL eCommerce parcels to the US" when the figure is that carrier's, "Parcels to the US" otherwise
  function groupName(facts, parcel) {
    var deliver = facts.timing && facts.timing.deliver;
    if (!deliver) return null;
    var code = deliver.group.indexOf('|') > -1 ? deliver.group.split('|')[1] : null;
    var carrier = code && parcel && parcel.carrierCode === code ? parcel.carrier : null;
    return (carrier ? carrier + ' parcels' : 'Parcels') + ' to ' + regionName(facts);
  }
  function timingFoot(facts) {
    var timing = facts.timing;
    if (!timing || !timing.ship) return '';
    return 'From ' + plural(timing.ship.n, 'order') + ' shipped in the last ' + weeksOf(timing) + ' weeks. Recomputed nightly. Not a promise.';
  }
  function noFigureSentence(facts) {
    return 'We have had too few recent parcels to ' + regionName(facts) + ' to quote a typical time, and we would rather not guess. Once it ships, this page follows the carrier scan by scan.';
  }

  /* ---------- the words ---------- */

  function carrierOf(parcel) {
    return parcel && parcel.carrier ? parcel.carrier : 'the carrier';
  }

  function statusCopy(facts) {
    var status = facts.status;
    var parcels = facts.parcels || [];
    var moving = parcels.filter(function (p) { return p.shipped && !p.delivered; })[0] || parcels.filter(function (p) { return p.shipped; })[0] || null;
    var slowText = 'Your order has been in the print queue longer than usual. We have asked the print partner about it and will email you with what we learn. Nothing you need to do.';

    if (status.key === 'cancelled') {
      return { tone: 'muted', title: 'Cancelled', text: 'This order was cancelled. If that comes as a surprise, write to us and we will sort it out.' };
    }
    if (status.key === 'paused') {
      return { tone: 'warn', title: 'Paused: we need a quick word', text: 'We paused this order before printing it, usually to double-check the shipping address. Look for an email from us; one short reply and it is back in the queue.' };
    }
    if (status.key === 'received') {
      if (status.slow) return { tone: 'warn', title: 'Taking longer than usual to print', text: slowText };
      // Answered from Shopify: the print shop has not been sent the order yet
      return facts.order.source === 'shopify'
        ? { tone: 'now', title: 'Order received', text: 'We have your order and are giving it a quick check before it joins the print queue. Every piece is made to order. You will get an email with tracking the moment it leaves the print shop.' }
        : { tone: 'now', title: 'Received, in the print queue', text: 'We have your order and it is lined up for printing. Every piece is made to order. You will get an email with tracking the moment it leaves the print shop.' };
    }
    if (status.key === 'printing') {
      return status.slow
        ? { tone: 'warn', title: 'Taking longer than usual to print', text: slowText }
        : { tone: 'now', title: 'Being printed', text: 'Your order is on the print floor. A tracking email follows as soon as it ships, and this page will show the parcel.' };
    }
    if (status.key === 'partial') {
      var delivered = parcels.filter(function (p) { return p.delivered; }).length;
      var onWay = parcels.filter(function (p) { return p.shipped && !p.delivered; }).length;
      var waiting = parcels.filter(function (p) { return !p.shipped; }).length;
      var bits = [];
      if (delivered) bits.push(words(delivered) + ' ' + (delivered === 1 ? 'parcel' : 'parcels') + ' delivered');
      if (onWay) bits.push(words(onWay) + ' ' + (onWay === 1 ? 'parcel' : 'parcels') + ' on ' + (onWay === 1 ? 'its' : 'their') + ' way');
      if (waiting) bits.push(words(waiting) + ' still being printed');
      return {
        tone: status.late ? 'warn' : 'now',
        title: capital(bits.join(', ')),
        text: 'Your order ships in ' + words(parcels.length) + ' parcels from different print facilities. Each one gets its own tracking email and shows below.' + (status.late ? ' One of them is taking longer than expected; see its card.' : '')
      };
    }
    if (status.key === 'delivered') {
      var last = parcels.filter(function (p) { return p.deliveredAt; }).sort(function (a, b) { return a.deliveredAt < b.deliveredAt ? 1 : -1; })[0] || moving;
      return {
        tone: 'ok',
        title: 'Delivered',
        text: 'Delivered' + (last && last.deliveredAt ? ' ' + fmtDateTime(last.deliveredAt) : '') + (last ? ' by ' + carrierOf(last) : '') + '. Not there? Parcels are sometimes scanned a few hours early. Check with neighbours or the mailroom, then write to us.'
      };
    }
    if (status.late && moving) {
      var since = daysSince(moving.lastScanAt);
      return {
        tone: 'warn',
        title: 'Taking longer than expected',
        text: 'Shipped ' + fmtDate(moving.shippedAt) + ' with ' + carrierOf(moving) + (moving.lastScanAt ? '; the last scan was ' + fmtDate(moving.lastScanAt) + (since !== null ? ', ' + plural(since, 'day') + ' ago' : '') : '') + '. That is past the usual time for parcels like this. Carriers do miss scans and most parcels like this still arrive. We are watching it too and will write to you with next steps if it does not move, so there is nothing you need to do yet.'
      };
    }
    if (status.key === 'in_transit' && moving) {
      var latest = moving.events && moving.events[0];
      return {
        tone: 'now',
        title: 'In transit',
        text: 'With ' + carrierOf(moving) + '.' + (latest ? ' Last scan ' + fmtDateTime(latest.at) + ': ' + latest.text.replace(/\.$/, '') + '.' : '')
      };
    }
    if (moving) {
      return { tone: 'now', title: 'Shipped', text: 'Handed to ' + carrierOf(moving) + (moving.shippedAt ? ' on ' + fmtDate(moving.shippedAt) : '') + '. The first carrier scans usually appear within a day.' };
    }
    return { tone: 'now', title: 'Shipped', text: 'On its way. The first carrier scans usually appear within a day.' };
  }

  /* ---------- drawing ---------- */

  function renderSteps(facts) {
    var byKey = {};
    (facts.steps || []).forEach(function (step) { byKey[step.key] = step; });
    return '<ol class="hh-track__steps" aria-label="Order progress">' + STEPS.map(function (def) {
      var step = byKey[def.key] || { state: 'todo', date: null };
      var state = step.state === 'done' && def.key === 'delivered' ? 'ok' : step.state;
      return '<li class="hh-track__step hh-track__step--' + esc(state) + '"><span class="hh-track__step-label">' + esc(def.label) + '</span><span class="hh-track__step-date">' + esc(step.date ? fmtDate(step.date) : '') + '</span></li>';
    }).join('') + '</ol>';
  }

  function renderMini(parcel) {
    var states;
    if (parcel.delivered) states = ['done', 'done', 'done', 'done', 'ok'];
    else if (parcel.inTransit) states = ['done', 'done', 'done', 'now', 'todo'];
    else if (parcel.shipped) states = ['done', 'done', 'now', 'todo', 'todo'];
    else states = ['done', 'now', 'todo', 'todo', 'todo'];
    return '<ol class="hh-track__mini" aria-hidden="true">' + states.map(function (state) { return '<li class="hh-track__mini-step hh-track__mini-step--' + state + '"></li>'; }).join('') + '</ol>' +
      '<div class="hh-track__mini-labels"><span>Ordered</span><span>Delivered</span></div>';
  }

  function renderItems(items, label) {
    if (!items || !items.length) return '';
    return '<div class="hh-track__items-block"><p class="hh-track__eyebrow">' + esc(label || 'Items') + '</p><ul class="hh-track__items">' + items.map(function (item) {
      return '<li class="hh-track__item">' +
        (item.image ? '<img class="hh-track__item-image" src="' + esc(item.image) + '" alt="" width="56" height="56" loading="lazy">' : '<span class="hh-track__item-image" aria-hidden="true"></span>') +
        '<div class="hh-track__item-text"><div class="hh-track__item-title">' + esc(item.title) + '</div>' + (item.variant ? '<div class="hh-track__item-variant">' + esc(item.variant) + '</div>' : '') + '</div>' +
        '<span class="hh-track__item-qty">× ' + esc(item.qty) + '</span></li>';
    }).join('') + '</ul></div>';
  }

  function renderTiming(facts) {
    var timing = facts.timing;
    if (!timing || !timing.ship) return '';
    var abroad = facts.order.region !== 'US';
    var rows = '<dt>Print shop</dt><dd>Orders placed in the last ' + weeksOf(timing) + ' weeks left the print shop in ' + rangeOf(timing.ship) + (abroad ? ', across all our orders' : '') + '. A few took up to ' + upToOf(timing.ship) + '.</dd>';
    rows += '<dt>Delivery</dt><dd>' + (timing.deliver
      ? 'Then ' + rangeOf(timing.deliver) + ' with the carrier for parcels to ' + esc(regionName(facts)) + '. A few took up to ' + upToOf(timing.deliver) + '.'
      : esc(noFigureSentence(facts))) + '</dd>';
    return '<div class="hh-track__panel hh-track__panel--timing"><div class="hh-track__panel-head"><h2 class="hh-track__panel-title">Typical timing</h2><span class="hh-track__panel-sub">from our recent orders</span></div><dl class="hh-track__kv">' + rows + '</dl><p class="hh-track__foot">' + esc(timingFoot(facts)) + '</p></div>';
  }

  function renderParcel(facts, parcel, total) {
    var head = total > 1 ? 'Parcel ' + parcel.n + ' of ' + total : 'Your parcel';
    var html = '<div class="hh-track__panel hh-track__parcel' + (parcel.late ? ' hh-track__parcel--late' : '') + '">';
    html += '<div class="hh-track__panel-head"><h2 class="hh-track__panel-title">' + esc(head) + '</h2><span class="hh-track__panel-sub">' + (parcel.shipped ? esc(parcel.carrier) + (parcel.service ? ' · ' + esc(parcel.service) : '') : 'Being printed') + '</span></div>';
    if (total > 1) html += renderMini(parcel);

    if (parcel.shipped) {
      html += '<dl class="hh-track__kv">';
      if (parcel.tracking) {
        html += '<dt>Tracking number</dt><dd><span class="hh-track__tracking"><code>' + esc(parcel.tracking) + '</code><button type="button" class="hh-track__link-button" data-hh-track-copy="' + esc(parcel.tracking) + '">Copy</button><span class="hh-track__copied" hidden>Copied</span></span></dd>';
      }
      html += '<dt>Shipped</dt><dd>' + esc(fmtDate(parcel.shippedAt)) + '</dd>';
      if (parcel.delivered) {
        html += '<dt>Delivered</dt><dd>' + esc(parcel.deliveredAt ? fmtDateTime(parcel.deliveredAt) : 'Yes, per the carrier') + '</dd>';
      } else {
        var deliver = facts.timing && facts.timing.deliver;
        var group = groupName(facts, parcel);
        var sentence;
        if (deliver && group) {
          var slowEnd = plusDays(parcel.shippedAt, Math.round(deliver.p80));
          sentence = group + ' have been arriving ' + rangeOf(deliver) + ' after shipping. A few took up to ' + upToOf(deliver) + '.' +
            (slowEnd ? (parcel.late ? ' The slow end of that range, ' + fmtDate(slowEnd) + ', has gone by.' : ' For this parcel the slow end of the range is <b>' + esc(fmtDate(slowEnd)) + '</b>. <span class="hh-track__soft">Measured on our own orders, not a promise.</span>') : '');
        } else {
          sentence = esc(noFigureSentence(facts));
        }
        html += '<dt>Typical timing</dt><dd>' + sentence + '</dd>';
      }
      var latest = parcel.events && parcel.events[0];
      if (latest) html += '<dt>Latest</dt><dd>' + esc(latest.text) + ' <span class="hh-track__soft">· ' + esc(fmtDateTime(latest.at)) + '</span></dd>';
      var link = parcel.carrierUrl || parcel.trackingUrl;
      if (link) html += '<dt>Carrier page</dt><dd><a href="' + esc(link) + '" target="_blank" rel="noopener">' + (parcel.carrierUrl ? 'Track on the ' + esc(parcel.carrier) + ' site' : 'Open the tracking page') + '</a></dd>';
      html += '</dl>';
      if (parcel.events && parcel.events.length) {
        html += '<details class="hh-track__events"><summary>All updates (' + parcel.events.length + ')</summary><ul class="hh-track__event-list">' + parcel.events.map(function (event) {
          return '<li><time datetime="' + esc(event.at) + '">' + esc(fmtDateTime(event.at)) + '</time><span>' + esc(event.text) + '</span></li>';
        }).join('') + '</ul></details>';
      }
    } else if (facts.timing && facts.timing.ship) {
      html += '<dl class="hh-track__kv"><dt>Typical timing</dt><dd>Orders placed in the last ' + weeksOf(facts.timing) + ' weeks left the print shop in ' + rangeOf(facts.timing.ship) + '. A few took up to ' + upToOf(facts.timing.ship) + '.</dd></dl>';
    }
    if (parcel.reshipment) html += '<p class="hh-track__foot hh-track__foot--note">This parcel is a reprint, sent at no cost to you.</p>';
    html += renderItems(parcel.items, 'In this parcel');
    html += '</div>';
    return html;
  }

  // Same list as histohead-relay src/withdraw.js: the EU 27, outermost regions Shopify lists apart, the EEA
  var WITHDRAWAL_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GP', 'MQ', 'GF', 'RE', 'YT', 'MF', 'AX', 'IS', 'LI', 'NO'];

  function renderHelp(facts, element) {
    var support = element.dataset.support || '';
    var contact = element.dataset.contactUrl || '';
    var shipping = element.dataset.shippingUrl || '';
    var returns = element.dataset.returnsUrl || '';
    var withdraw = element.dataset.withdrawUrl || '';
    var mail = support ? (contact ? '<a href="' + esc(contact) + '">' + esc(support) + '</a>' : '<a href="mailto:' + esc(support) + '">' + esc(support) + '</a>') : 'us';
    var links = [];
    if (shipping) links.push('<a href="' + esc(shipping) + '">Shipping &amp; Delivery</a>');
    if (returns) links.push('<a href="' + esc(returns) + '">Returns &amp; Refunds</a>');
    // The EU withdrawal page with the number filled in (the number only: the email or postcode stays
    // here), for orders delivered where the EU right applies (the relay's WITHDRAWAL_COUNTRIES)
    var eu = WITHDRAWAL_COUNTRIES.indexOf(String(facts.order.country || '').toUpperCase()) !== -1;
    var cancel = withdraw && eu && facts.status.key !== 'cancelled'
      ? '<p>Changed your mind? Under the EU right of withdrawal you can <a href="' + esc(withdraw) + '#o=' + encodeURIComponent(facts.order.number) + '">withdraw from this order</a>.</p>' : '';
    return '<div class="hh-track__help"><p>Questions about this order? Reply to your confirmation email or write to ' + mail + ', quoting <b>' + esc(facts.order.number) + '</b>.</p>' +
      cancel + (links.length ? '<p>Also useful: ' + links.join(' and ') + '.</p>' : '') + '</div>';
  }

  function render(facts, element) {
    var copy = statusCopy(facts);
    var parcels = facts.parcels || [];
    var showParcels = parcels.some(function (p) { return p.shipped; });
    var count = facts.order.itemCount || (facts.items || []).length;
    var html = '<div class="hh-track__order-head"><h2 class="hh-track__order-number">Order ' + esc(facts.order.number) + '</h2><span class="hh-track__order-meta">Placed ' + esc(fmtDate(facts.order.placed)) + ' · ' + esc(plural(count, 'item')) + (facts.order.town ? ' · to ' + esc(facts.order.town) : '') + '</span></div>';
    html += '<div class="hh-track__status hh-track__status--' + esc(copy.tone) + '"><p class="hh-track__eyebrow">Current status</p><h2 class="hh-track__status-title">' + esc(copy.title) + '</h2><p class="hh-track__status-text">' + esc(copy.text) + '</p></div>';
    if (facts.status.key !== 'partial' && facts.status.key !== 'cancelled') html += renderSteps(facts);
    if (!showParcels && (facts.status.key === 'received' || facts.status.key === 'printing')) html += renderTiming(facts);
    if (showParcels) {
      html += '<div class="hh-track__parcels">' + parcels.map(function (parcel) { return renderParcel(facts, parcel, parcels.length); }).join('') + '</div>';
    } else {
      html += renderItems(facts.items, 'Items');
    }
    html += renderHelp(facts, element);
    return html;
  }

  /* ---------- the element ---------- */

  var NOT_FOUND = '<strong>No order matches that number and the email or postcode you gave.</strong> The number starts with HH followed by digits (the letter O and the digit 0 are easy to mix up). The email has to be the one used at checkout, or use the postcode the parcel was sent to. Just ordered? A new order can take a few minutes to appear here.';
  var SLOW_DOWN = '<strong>Too many tries just now.</strong> Wait a few minutes and try again.';
  var DOWN = '<strong>We cannot reach the tracking service right now.</strong> Your shipping email has the carrier link; or try again in a few minutes.';

  class HHTrackOrder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-hh-track-form]');
      this.orderInput = this.querySelector('#hh-track-order');
      this.keyInput = this.querySelector('#hh-track-key');
      this.submit = this.querySelector('[data-hh-track-submit]');
      this.submitLabel = this.querySelector('[data-hh-track-submit-label]');
      this.error = this.querySelector('[data-hh-track-error]');
      this.result = this.querySelector('[data-hh-track-result]');
      this.fallback = this.querySelector('[data-hh-track-fallback]');
      this.endpoint = this.dataset.endpoint || '/apps/hh/track';
      this.busy = false;
      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.result.addEventListener('click', this.onResultClick.bind(this));
      this.prefill();
    }

    // #o=HH1290&k=32773 from the shipping email, or #o=HH1290 alone from an email that knows only the
    // number. The fragment is dropped from the address bar once read, so later page analytics never see it.
    prefill() {
      var hash = (window.location.hash || '').replace(/^#/, '');
      if (!hash) return;
      var params;
      try { params = new URLSearchParams(hash); } catch (error) { return; }
      var order = params.get('o') || params.get('order');
      var key = params.get('k') || params.get('key');
      if (!order && !key) return;
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (error) { /* the address bar keeps the fragment; nothing else changes */ }
      if (order) this.orderInput.value = order;
      if (key) this.keyInput.value = key;
      if (order && key) this.ask();
      else if (order) this.keyInput.focus();
    }

    onSubmit(event) {
      event.preventDefault();
      this.ask();
    }

    showError(html) {
      this.error.innerHTML = html;
      this.error.hidden = false;
      this.result.hidden = true;
      this.result.innerHTML = '';
    }

    setBusy(busy) {
      this.busy = busy;
      this.submit.disabled = busy;
      this.submit.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (this.submitLabel) {
        if (busy) { this.submitLabel.dataset.label = this.submitLabel.textContent; this.submitLabel.textContent = 'Looking…'; }
        else if (this.submitLabel.dataset.label) this.submitLabel.textContent = this.submitLabel.dataset.label;
      }
    }

    ask() {
      if (this.busy) return;
      var order = this.orderInput.value.trim();
      var key = this.keyInput.value.trim();
      this.orderInput.removeAttribute('aria-invalid');
      this.keyInput.removeAttribute('aria-invalid');
      if (this.fallback) this.fallback.hidden = true;
      if (!order || !key) {
        if (!order) this.orderInput.setAttribute('aria-invalid', 'true');
        if (!key) this.keyInput.setAttribute('aria-invalid', 'true');
        this.showError('<strong>Both fields are needed.</strong> Your order number (it starts with HH) and either the email you used at checkout or the delivery postcode.');
        (order ? this.keyInput : this.orderInput).focus();
        return;
      }
      this.error.hidden = true;
      this.setBusy(true);
      var self = this;
      window.fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ order: order, key: key })
      }).then(function (response) {
        return response.json().then(function (body) { return { status: response.status, body: body }; }, function () { return { status: response.status, body: null }; });
      }).then(function (answer) {
        self.setBusy(false);
        self.handle(answer);
      }, function () {
        self.setBusy(false);
        self.handle({ status: 0, body: null });
      });
    }

    handle(answer) {
      var body = answer.body;
      if (answer.status === 200 && body && body.ok) {
        this.error.hidden = true;
        this.result.innerHTML = render(body, this);
        this.result.hidden = false;
        this.result.focus({ preventScroll: true });
        var top = this.result.getBoundingClientRect().top + window.pageYOffset - 24;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }
      if (answer.status === 404) {
        this.orderInput.setAttribute('aria-invalid', 'true');
        this.keyInput.setAttribute('aria-invalid', 'true');
        this.showError(NOT_FOUND);
        return;
      }
      if (answer.status === 400) {
        this.orderInput.setAttribute('aria-invalid', 'true');
        this.showError('<strong>That does not look like an order number.</strong> It starts with HH followed by digits, like HH1290, and sits at the top of your confirmation email.');
        return;
      }
      if (answer.status === 429) {
        this.showError(SLOW_DOWN);
        return;
      }
      this.showError(DOWN);
      if (this.fallback) this.fallback.hidden = false;
    }

    onResultClick(event) {
      var button = event.target.closest('[data-hh-track-copy]');
      if (!button) return;
      var text = button.getAttribute('data-hh-track-copy');
      var done = button.nextElementSibling;
      var show = function () {
        if (!done) return;
        done.hidden = false;
        window.setTimeout(function () { done.hidden = true; }, 1600);
      };
      var select = function () {
        var code = button.previousElementSibling;
        if (!code || !window.getSelection) return;
        var range = document.createRange();
        range.selectNodeContents(code);
        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(show, select);
      else select();
    }
  }

  customElements.define('hh-track-order', HHTrackOrder);
})();
